import React, { useState, useMemo } from 'react';
import { useAppContext } from '../../context/AppContext';
import RecordRow from './RecordRow';
import TableHeader from './TableHeader';
import EditRecordModal from './EditRecordModal';
import { Search, Plus, FileText, RefreshCw, X } from 'lucide-react';
import { Record } from '../../types';

const TableView: React.FC = () => {
  const { columns, records, loading, refreshData } = useAppContext();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<Record | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortConfig, setSortConfig] = useState<{ column: string; direction: 'asc' | 'desc' } | null>(null);

  // Get all unique tags from records
  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    records.forEach(record => {
      if (Array.isArray(record.tags)) {
        record.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [records]);

  // Get primary key column and visible columns
  const primaryKeyColumn = useMemo(() => {
    return columns.find(col => col.is_primary_key)?.name || 'id';
  }, [columns]);

  const visibleColumns = useMemo(() => {
    return columns.filter(col => !['id', 'user_id'].includes(col.name));
  }, [columns]);

  // Filter and sort records
  const filteredAndSortedRecords = useMemo(() => {
    let result = [...records];
    
    // Apply text search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(record => {
        const nameMatch = record.name?.toLowerCase().includes(query);
        const contentMatch = record.content?.toLowerCase().includes(query);
        const notesMatch = record.notes?.toLowerCase().includes(query);
        return nameMatch || contentMatch || notesMatch;
      });
    }

    // Apply tag filters
    if (selectedTags.length > 0) {
      result = result.filter(record => 
        selectedTags.every(tag => record.tags?.includes(tag))
      );
    }
    
    // Apply sorting
    if (sortConfig) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.column];
        const bValue = b[sortConfig.column];
        
        if (aValue === null) return sortConfig.direction === 'asc' ? -1 : 1;
        if (bValue === null) return sortConfig.direction === 'asc' ? 1 : -1;
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        return sortConfig.direction === 'asc'
          ? (aValue > bValue ? 1 : -1)
          : (aValue < bValue ? 1 : -1);
      });
    }
    
    return result;
  }, [records, searchQuery, selectedTags, sortConfig]);

  const handleSort = (column: string) => {
    setSortConfig(current => {
      if (current?.column === column) {
        return current.direction === 'asc'
          ? { column, direction: 'desc' }
          : null;
      }
      return { column, direction: 'asc' };
    });
  };

  const handleEditRecord = (record: Record) => {
    setCurrentRecord(record);
    setIsEditModalOpen(true);
  };

  const handleAddRecord = () => {
    setCurrentRecord(null);
    setIsEditModalOpen(true);
  };

  const handleTagSelect = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const formatRelativeTime = (date: string) => {
    const now = new Date();
    const updateDate = new Date(date);
    const diffInHours = Math.floor((now.getTime() - updateDate.getTime()) / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInMonths = Math.floor(diffInDays / 30);

    let relativeTime = '';
    if (diffInMonths > 0) {
      relativeTime = `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
    } else if (diffInDays > 0) {
      relativeTime = `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    } else if (diffInHours > 0) {
      relativeTime = `${diffInHours} ${diffInHours === 1 ? 'hr' : 'hrs'} ago`;
    } else {
      relativeTime = 'just now';
    }

    return `${updateDate.toLocaleDateString()} (${relativeTime})`;
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <FileText className="w-8 h-8 mr-3 text-emerald-500" />
            Content Library
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage and organize your content blocks
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search name, content, or notes..."
              className="pl-10 pr-4 py-2.5 w-64 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-shadow"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            onClick={handleAddRecord}
            className="flex items-center px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors font-medium"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Content
          </button>
        </div>
      </div>

      {availableTags.length > 0 && (
        <div className="mb-6">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Filter by tags:
          </div>
          <div className="flex flex-wrap gap-2">
            {availableTags.map(tag => (
              <button
                key={tag}
                onClick={() => handleTagSelect(tag)}
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedTags.includes(tag)
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {tag}
                {selectedTags.includes(tag) && (
                  <X className="w-4 h-4 ml-2" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {loading ? (
          <div className="col-span-full flex justify-center items-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-emerald-500" />
          </div>
        ) : filteredAndSortedRecords.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No content found</h3>
            <p className="text-gray-500 dark:text-gray-400">
              {searchQuery || selectedTags.length > 0 ? 'Try adjusting your search or filters' : 'Start by adding some content'}
            </p>
          </div>
        ) : (
          filteredAndSortedRecords.map((record) => (
            <div
              key={record[primaryKeyColumn]}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow cursor-pointer relative"
              onClick={() => handleEditRecord(record)}
            >
              {record.tags?.includes('ready') && (
                <div 
                  className="absolute top-4 right-4 w-3 h-3 rounded-full bg-emerald-500 ring-4 ring-emerald-500/20"
                  title="Ready for production"
                />
              )}
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 truncate pr-6">
                {record.name}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm line-clamp-3">
                {record.content}
              </p>
              {record.notes && (
                <p className="mt-2 text-sm text-gray-400 dark:text-gray-500 line-clamp-2 italic">
                  {record.notes}
                </p>
              )}
              {record.tags?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {record.tags.map((tag: string) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-4 text-xs text-gray-400 dark:text-gray-500">
                Created: {formatRelativeTime(record.created_at)}
              </div>
            </div>
          ))
        )}
      </div>

      <EditRecordModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        record={currentRecord}
        columns={columns}
        tableName="content_blocks"
        primaryKeyColumn={primaryKeyColumn}
        onSuccess={refreshData}
      />
    </div>
  );
};

export default TableView;